import {
  createStyles,
  FormControl,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core'
import axios from 'axios'
import React, { useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Button from '../components/Button/Button'
import Layout from '../components/Layout/Layout'
import TextField from '../components/TextField/TextField'
import { getTranslations } from '../i18n'
import { setFullLocale } from '../utils/utils'
import { Cart } from '../typings/base'
import TextBox from '../components/TextField/TextBox'
import clsx from 'clsx'
import CMSText, {
  isPrismicText,
  prismicText,
} from '../components/Global/CMSText'
import { createClient, getSingleDocument } from '../prismicio'

type Tprops = {
  main?: any
  doc?: any
  cart: Cart
  parameters: any
  basket: any
  funnel: any
  inStoreText: any
  accountRes?: any
  faqRes?: any
  banner?: any
}

const mapStateToProps = (state) => ({
  cart: state.cart,
  parameters: state.parameters,
})

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      background: theme.extraColors.greyLight,
      paddingTop: theme.spacing(10),
      paddingBottom: theme.spacing(10),
    },
    container: {
      background: theme.palette.common.white,
      maxWidth: 600,
      margin: 'auto',
      padding: theme.spacing(4),
    },
    alignCenter: {
      textAlign: 'center',
    },
    formControl: {
      minWidth: 120,
      width: '100%',
    },
    sizeSelector: {
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(0.5, 2, 0.5, 1),
      margin: theme.spacing(0.5),
      backgroundColor: theme.extraColors.greyLight,
      minWidth: 200,
      color: theme.palette.common.black,
      height: 40,
      //fontWeight: 'bold',
      border: 'none',
    },
    textArea: {
      '& .input': {
        '& .MuiInput-root': {
          position: 'initial',
          width: '100%',
        },
        '& .MuiInput-underline': {
          '&:before, &:after': {
            display: 'none',
          },
        },
      },
    },
    label: {
      paddingLeft: theme.spacing(1.25),
      marginBottom: theme.spacing(0.5),
      '& .paragraph-class': {
        margin: 0,
      },
    },
    labelContainer: {
      cursor: 'pointer',
      fontSize: 9,
      minHeight: `calc(${theme.typography.body2.lineHeight}em * 2)}`, // Minimum 2 lines height based on theme property. For a weird reason this CSS declaration must be the latest.
    },
    error: {
      background: 'none', // Weirdest bug if no CSS prop set, impossible to enable .input sibbling
      '& .input': {
        borderColor: theme.palette.error.main,
      },
    },
  })
)

const Contact = ({
  main,
  doc,
  inStoreText,
  basket,
  accountRes,
  faqRes,
  banner,
}: Tprops) => {
  const classes = useStyles()
  const [firstname, setFirstname] = useState('')
  const [lastname, setLastname] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [firstList, setFirstList] = useState('')
  const [secondList, setSecondList] = useState('')
  const [errorFields, setErrorFields] = useState(false)
  const [mailSend, setMailSend] = useState(false)
  const [errorMail, setErrorMail] = useState(false)
  const { t } = useTranslation()

  const handleFirstList = (event) => {
    setFirstList(event.target.value)
  }

  const handleSecondList = (event) => {
    setSecondList(event.target.value)
  }

  const sendData = async () => {
    setMailSend(false)
    setErrorFields(false)
    setErrorMail(false)
    if (firstname !== '' && lastname !== '' && email !== '' && message !== '') {
      //send
      const response = await axios({
        method: 'post',
        url: process.env.CONTACT_FORM_URL,
        data: {
          firstname: firstname,
          lastname: lastname,
          shop: process.env.STORE,
          email: email,
          message: message,
          firstList: firstList,
          secondList: secondList,
        },
      })
      if (response.status === 200) {
        setMailSend(true)
        setFirstname('')
        setLastname('')
        setEmail('')
        setMessage('')
        setFirstList('')
        setSecondList('')
        setErrorFields(false)
      } else setErrorMail(true)
    } else {
      setErrorFields(true)
    }
  }

  return (
    <>
      <Layout
        mainLogo={main?.data?.logo}
        doc={main}
        t={t}
        inStoreText={inStoreText}
        data-testid="confirmation"
        metaTitle={prismicText(
          doc?.data?.meta_title_contact,
          t('texts:account:herotitle')
        )}
        metaDescription={doc?.data?.meta_description_contact}
        metaKeywords={doc?.data?.meta_keywords_contact}
        hideTop
        basket={basket}
        account={accountRes}
        faq={faqRes}
        banner={banner}
      >
        <section className={`contact-page ${classes.root}`}>
          <div className={classes.container}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography
                  variant={'h2'}
                  className={'contact-page__title'}
                  align="center"
                >
                  {prismicText(
                    doc?.data?.title_contact,
                    t('texts:customer_survey:main_title')
                  )}
                </Typography>
              </Grid>
              {doc?.data?.desc_contact?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant={'body1'} align="center">
                    {prismicText(
                      doc?.data?.desc_contact,
                      t('texts:customer_survey:desc_contact')
                    )}
                  </Typography>
                </Grid>
              )}
              <Grid
                item
                xs={6}
                className={clsx(
                  errorFields &&
                    firstname === '' && ['field--error', classes.error]
                )}
              >
                <TextField
                  label={`${t('texts:account:firstname')} *`}
                  type="text"
                  name="firstname"
                  placeholder={t('texts:account:firstname')}
                  value={firstname}
                  required={true}
                  onChange={setFirstname}
                />
              </Grid>
              <Grid
                item
                xs={6}
                className={clsx(
                  errorFields &&
                    lastname === '' && ['field--error', classes.error]
                )}
              >
                <TextField
                  label={`${t('texts:account:lastname')} *`}
                  type="text"
                  name="lastname"
                  placeholder={t('texts:account:lastname')}
                  value={lastname}
                  required={true}
                  onChange={setLastname}
                />
              </Grid>
              <Grid
                item
                xs={12}
                className={clsx(
                  errorFields && email === '' && ['field--error', classes.error]
                )}
              >
                <TextField
                  label={`${t('texts:account:email')} *`}
                  type="text"
                  name="email"
                  placeholder={t('texts:account:email')}
                  value={email}
                  required={true}
                  onChange={setEmail}
                />
              </Grid>
              {doc?.data?.list_1_label?.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <FormControl
                    variant="outlined"
                    className={classes.formControl}
                  >
                    <label className={clsx('label', classes.label)}>
                      <CMSText data={doc.data.list_1_label} />
                    </label>
                    <select
                      value={firstList}
                      onChange={handleFirstList}
                      className={clsx('contact_select', classes.sizeSelector)}
                    >
                      <option aria-label="None" value="" />
                      {doc?.data?.options_list_1?.length > 0 &&
                        doc?.data?.options_list_1.map((option, index) => {
                          return (
                            <option
                              key={index}
                              value={prismicText(option?.option)}
                            >
                              {prismicText(option?.option)}
                            </option>
                          )
                        })}
                    </select>
                  </FormControl>
                </Grid>
              )}
              {doc?.data?.list_2_label?.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <FormControl
                    variant="outlined"
                    className={classes.formControl}
                  >
                    <label className={clsx('label', classes.label)}>
                      <CMSText data={doc.data.list_2_label} />
                    </label>
                    <select
                      value={secondList}
                      onChange={handleSecondList}
                      className={clsx('contact_select', classes.sizeSelector)}
                    >
                      <option aria-label="None" value="" />
                      {doc?.data?.options_list_2?.length > 0 &&
                        doc?.data?.options_list_2.map((option, index) => {
                          return (
                            <option
                              key={index}
                              value={prismicText(option?.option)}
                            >
                              {prismicText(option?.option)}
                            </option>
                          )
                        })}
                    </select>
                  </FormControl>
                </Grid>
              )}
              <Grid
                item
                xs={12}
                className={clsx(
                  errorFields &&
                    message === '' && ['field--error', classes.error]
                )}
              >
                <TextField
                  multiline
                  textArea
                  rows={4}
                  rowsMax={4}
                  type={'textarea'}
                  name={'message'}
                  value={message}
                  onChange={setMessage}
                  placeholder={t('texts:customer_survey:message')}
                  label={`${t('texts:customer_survey:message')} *`}
                  className={clsx('textarea', classes.textArea)}
                />
              </Grid>
              <Grid item xs={12} className={classes.alignCenter}>
                {errorFields && (
                  <Typography variant={'body1'}>
                    {t('texts:customer_survey:error_fields')}
                  </Typography>
                )}
                {mailSend && (
                  <Typography variant={'body1'}>
                    {t('texts:account:email_send')}
                  </Typography>
                )}
                {errorMail && (
                  <Typography variant={'body1'}>
                    {t('texts:account:error_happend')}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} className={classes.alignCenter}>
                <Button
                  variant={'contained'}
                  color={'secondary'}
                  onClick={() => sendData()}
                >
                  {t('texts:customer_survey:send')}
                </Button>
              </Grid>
            </Grid>
            {isPrismicText(doc?.data?.data_text) && (
              <TextBox text={doc?.data?.data_text} />
            )}
          </div>
        </section>
      </Layout>
    </>
  )
}

export async function getStaticProps({ locale, previewData }) {
  const client = createClient({ previewData })
  const lang = setFullLocale(locale)

  const general = await getSingleDocument(client, lang, 'general')
  const doc = await getSingleDocument(client, lang, 'contact_form')
  const basket = await getSingleDocument(client, lang, 'basket')
  const funnel = await getSingleDocument(client, lang, 'funnel')
  const inStoreText = await getSingleDocument(client, lang, 'instore')
  const accountRes = await getSingleDocument(client, lang, 'account')
  const faqRes = await getSingleDocument(client, lang, 'faq')
  const banner = await getSingleDocument(client, lang, 'banner')

  return {
    props: {
      main: general,
      basket,
      funnel,
      doc,
      inStoreText,
      accountRes,
      faqRes,
      banner,
      i18nResources: await getTranslations(locale, ['texts']),
    },
    revalidate: 300,
  }
}

export default connect(mapStateToProps)(Contact)
